import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sendEmail } from "./emailService";
import type { Lang } from "./emailTemplates";

// ── Shared context type ────────────────────────────────────────────────────────

interface AutomationContext {
  email: string;
  first_name?: string;
  last_name?: string;
  language?: Lang;
  leadId?: string;
  clientId?: string;
  [key: string]: string | undefined;
}

// ── Named trigger functions ────────────────────────────────────────────────────

/** Lead just submitted a contact / inquiry form. */
export async function onNewLead(ctx: AutomationContext): Promise<void> {
  const { email, language = "en", leadId, ...data } = ctx;
  const result = await sendEmail({
    to: email,
    templateId: "inquiryResponse",
    data: { ...data, email },
    language,
    leadId,
  });
  if (!result.success) console.error("[onNewLead]", result.error);
}

/** Any public form submitted (contact, brief, etc.) — generic confirmation. */
export async function onFormSubmitted(ctx: AutomationContext): Promise<void> {
  const { email, language = "en", leadId, ...data } = ctx;
  const result = await sendEmail({
    to: email,
    templateId: "inquiryResponse",
    data: { ...data, email },
    language,
    leadId,
  });
  if (!result.success) console.error("[onFormSubmitted]", result.error);
}

/** A booking / discovery call has been confirmed. */
export async function onBookingConfirmed(ctx: AutomationContext & { booking_date?: string; booking_time?: string }): Promise<void> {
  const { email, language = "en", leadId, ...data } = ctx;
  const result = await sendEmail({
    to: email,
    templateId: "discoveryCallConfirmation",
    data: { ...data, email },
    language,
    leadId,
  });
  if (!result.success) console.error("[onBookingConfirmed]", result.error);
}

/** Lead converted to client — send welcome / onboarding email. */
export async function onLeadConverted(ctx: AutomationContext & { portal_link?: string }): Promise<void> {
  const { email, language = "en", leadId, clientId, ...data } = ctx;
  const result = await sendEmail({
    to: email,
    templateId: "welcomeEmail",
    data: { ...data, email },
    language,
    leadId,
    clientId,
  });
  if (!result.success) console.error("[onLeadConverted]", result.error);
}

/** Contract sent to client for review/signing. */
export async function onContractSigned(ctx: AutomationContext & { package?: string }): Promise<void> {
  const { email, language = "en", clientId, ...data } = ctx;
  const result = await sendEmail({
    to: email,
    templateId: "contractSigned",
    data: { ...data, email },
    language,
    clientId,
  });
  if (!result.success) console.error("[onContractSigned]", result.error);
}

/** Payment received — send receipt / confirmation. */
export async function onPaymentReceived(ctx: AutomationContext & { amount?: string; month?: string }): Promise<void> {
  const { email, language = "en", clientId, ...data } = ctx;
  const result = await sendEmail({
    to: email,
    templateId: "paymentReceived",
    data: { ...data, email },
    language,
    clientId,
  });
  if (!result.success) console.error("[onPaymentReceived]", result.error);
}

// ── Legacy fireAutomation (preserved for BookConsultation + AdminDashboard) ────

const TRIGGER_TO_TEMPLATE: Record<string, string> = {
  lead_created: "lead-confirmation",
  project_created: "welcome-onboarding",
  proposal_sent: "proposal-sent",
  contract_created: "contract-ready",
  invoice_created: "invoice-created",
  invoice_overdue: "invoice-overdue",
  project_status_change: "project-status-update",
  project_completed: "project-complete",
};

export const fireAutomation = async (
  triggerEvent: string,
  context: {
    first_name?: string;
    last_name?: string;
    email?: string;
    project_name?: string;
    project_status?: string;
    amount?: string;
  }
) => {
  try {
    const { data: workflows } = await supabase
      .from("workflow_automations")
      .select("*")
      .eq("trigger_event", triggerEvent)
      .eq("is_active", true);

    if (!workflows || workflows.length === 0) {
      const templateName = TRIGGER_TO_TEMPLATE[triggerEvent];
      if (templateName && context.email) {
        await sendTransactionalEmail(templateName, context);
      }
      return;
    }

    for (const workflow of workflows) {
      await supabase
        .from("workflow_automations")
        .update({ last_triggered_at: new Date().toISOString() })
        .eq("id", workflow.id);

      if (workflow.action_type === "send_email") {
        const config = workflow.action_config as Record<string, unknown>;
        const templateName =
          (config?.template_name as string) || TRIGGER_TO_TEMPLATE[triggerEvent];

        if (!templateName || !context.email) continue;

        await sendTransactionalEmail(templateName, context, workflow.id);

        toast.success(`Automation: ${workflow.name}`, {
          description: `Email sent to ${context.email}`,
        });
      }
    }
  } catch (err) {
    console.error("[Automation] Error:", err);
  }
};

async function sendTransactionalEmail(
  templateName: string,
  context: {
    first_name?: string;
    last_name?: string;
    email?: string;
    project_name?: string;
    project_status?: string;
    amount?: string;
  },
  workflowId?: string
) {
  if (!context.email) return;

  void workflowId; // available for future logging

  const idempotencyKey = `${templateName}-${context.email}-${Date.now()}`;

  const { error } = await supabase.functions.invoke("send-transactional-email", {
    body: {
      templateName,
      recipientEmail: context.email,
      idempotencyKey,
      templateData: {
        first_name: context.first_name ?? "",
        last_name: context.last_name ?? "",
        project_name: context.project_name ?? "",
        project_status: context.project_status ?? "",
        amount: context.amount ?? "",
      },
    },
  });

  if (error) {
    console.error(`[Automation] Failed to send ${templateName}:`, error);
    toast.error(`Email failed: ${templateName}`, {
      description: error.message,
    });
  }
}
